import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';

import { RouteContext } from 'src/context';
import BillingDraftWidget from 'src/features/assistant/action-item/BillingDraftWidget';
import { useDefaultCurrency } from 'src/hooks/useDefaultCurrency';
import { useAppSelector } from 'src/hooks/useStore';
import { LineItem as LegacyLineItem } from 'src/legacy/components/billing/new/types';
import { usePaymentProcessingFee } from 'src/legacy/components/billing/new/usePaymentProcessingFee';
import { StripeCurrencyFormatterAdapter } from 'src/legacy/components/Formatters';
import { usePrimaryPaymentSource } from 'src/routes/billing/hooks/usePaymentSources';
import { usePaymentMethods } from 'src/routes/payments/hooks/usePaymentMethods';
import {
  InvoiceCollectionMethod,
  LineItemType,
  PaymentMethodType,
} from 'src/services/api/invoicesApi';
import { useGetProductsQuery } from 'src/services/api/productsApi';
import { RootState } from 'src/store';
import { CTA } from 'src/types/stream-chat';
import { DateUtils } from 'src/utils';
import { arrayToMap } from 'src/utils/array';
import InvoiceUtils from 'src/utils/InvoiceUtils';

interface Props {
  cta: CTA;
  onCTAClick: (cta: CTA) => void;
  widgetName: string;
  isSubscription?: boolean;
  buttonName: string;
}

const selector = createSelector(
  [
    (state: RootState) => state.clients.companies,
    (state: RootState) => state.activeCompany,
  ],
  (companies, activeCompany) => ({
    companies: arrayToMap(companies, 'id'),
    activeCompanyId: activeCompany.activeCompanyId ?? '',
  }),
);

export const BillingWidget = ({
  cta,
  onCTAClick,
  widgetName,
  isSubscription,
  buttonName,
}: Props) => {
  const { query, pathname } = useContext(RouteContext);
  const { clientUserId } = query;

  const companyId = pathname.split('/').at(2) || '';
  const { companies, activeCompanyId } = useAppSelector(selector);
  const company = companies.get(clientUserId || '');

  const recipientId = clientUserId || companyId || '';
  const { defaultSource: defaultSourceId, sources } = usePaymentMethods({
    recipientId: recipientId,
    companyId: company ? company.id : activeCompanyId || '',
  });

  const { selectedSource } = usePrimaryPaymentSource({
    defaultSourceId,
    sources,
  });

  // Get billing data based on whether this is an invoice or subscription
  const billingData = isSubscription ? cta.subscription_data : cta.invoice_data;

  const lineItems = useMemo(() => {
    if (!Array.isArray(billingData?.lineItems)) {
      return [];
    }

    // Transform CTA LineItems to Legacy LineItems
    return billingData.lineItems
      .filter((item) => item.productId || item.description) // Only include items with essential data
      .map(
        (item): LegacyLineItem => ({
          id: item.productId ?? item.description ?? '',
          type: item.productId ? LineItemType.Product : LineItemType.OneTime,
          productId: item.productId ?? undefined,
          priceId: item.priceId ?? undefined,
          quantity: String(item.quantity ?? 0),
          amount: String(item.amount ?? 0),
          description: item.description ?? '',
        }),
      );
  }, [billingData?.lineItems]);

  const formattedSubscriptionStartDate = useMemo(() => {
    const scheduleStartDateRaw =
      isSubscription && cta.subscription_data?.scheduleStartDate
        ? cta.subscription_data.scheduleStartDate
        : undefined;
    const scheduleStartDate = scheduleStartDateRaw
      ? moment(scheduleStartDateRaw).unix()
      : moment().unix();
    return moment.unix(scheduleStartDate).format('MMM DD, YYYY');
  }, [isSubscription, cta.subscription_data]);

  const formattedDueDate = useMemo(() => {
    if (isSubscription && cta.subscription_data) {
      const scheduleStartDateRaw = cta.subscription_data.scheduleStartDate;
      const scheduleStartDate = scheduleStartDateRaw
        ? moment(scheduleStartDateRaw).unix()
        : moment().unix();
      const dueDays = cta.subscription_data.daysUntilDue || 0;
      return moment
        .unix(scheduleStartDate)
        .add(Math.round(dueDays), 'days')
        .format('MMM DD, YYYY');
    } else if (!isSubscription && cta.invoice_data) {
      return (
        DateUtils.formatDate(cta.invoice_data.dueDate, 'MMM DD, YYYY') || ''
      );
    }
    return '';
  }, [isSubscription, cta.subscription_data, cta.invoice_data]);

  const paymentDetails = useMemo(() => {
    return {
      subTotal: InvoiceUtils.GetSubTotalOfLineitem(lineItems),
      tax: billingData?.taxPercentage
        ? parseFloat(String(billingData.taxPercentage))
        : 0,
      dueDate: isSubscription
        ? formattedDueDate
        : (!isSubscription &&
            cta.invoice_data?.collectionMethod ===
              InvoiceCollectionMethod.SendInvoice &&
            DateUtils.formatDate(cta.invoice_data.dueDate, 'MMM DD, YYYY')) ||
          '',
      dateOfIssue: isSubscription
        ? formattedSubscriptionStartDate
        : moment().format('MMM DD, YYYY'),
      memo: billingData?.memo || '',
      interval:
        isSubscription && cta.subscription_data?.interval
          ? cta.subscription_data.interval
          : '',
    };
  }, [
    billingData,
    lineItems,
    formattedDueDate,
    formattedSubscriptionStartDate,
    isSubscription,
    cta.invoice_data,
    cta.subscription_data,
  ]);
  const taxAmount = paymentDetails.subTotal
    ? paymentDetails.subTotal * (Number(paymentDetails.tax) / 100.0)
    : 0;
  const amountDue = paymentDetails.subTotal
    ? paymentDetails.subTotal + taxAmount
    : 0;
  const { fee, ignoreFees, label } = usePaymentProcessingFee({
    amountDue: amountDue,
    source: selectedSource,
    preferences: {
      absorbTransactionFee: [],
      paymentMethodTypes: [
        PaymentMethodType.CreditCard,
        PaymentMethodType.BankAccount,
      ],
    },
  });

  const defaultCurrency = useDefaultCurrency();

  const billingDetails = useMemo(() => {
    return {
      lineItems: lineItems,
      interval:
        isSubscription && cta.subscription_data?.interval
          ? cta.subscription_data.interval
          : undefined,
      currency: defaultCurrency,
      taxPercentage: String(billingData?.taxPercentage || ''),

      collectionMethod: String(billingData?.collectionMethod ?? ''),
      feeLabel: label || '',
      ignoreFees: ignoreFees,
      totalAmount: StripeCurrencyFormatterAdapter({
        value: amountDue + fee,
        currency: defaultCurrency,
      }),
      subTotal: StripeCurrencyFormatterAdapter({
        value: amountDue - taxAmount,
        currency: defaultCurrency,
      }),
      fees: StripeCurrencyFormatterAdapter({
        value: fee,
        currency: defaultCurrency,
      }),
      tax: StripeCurrencyFormatterAdapter({
        value: taxAmount,
        currency: defaultCurrency,
      }),
      taxAmount: taxAmount,
      fee: fee,
    };
  }, [
    lineItems,
    billingData,
    defaultCurrency,
    amountDue,
    taxAmount,
    fee,
    label,
    ignoreFees,
    isSubscription,
    cta.subscription_data,
  ]);
  const { data: products = [] } = useGetProductsQuery();

  const getProductImage = useCallback(
    (productId: string) => {
      const product = products.find((p) => p.id === productId);
      return product?.imageUrls[0] || '';
    },
    [products],
  );

  const avatarMap = useMemo(() => {
    return billingDetails.lineItems.reduce<Record<string, string>>(
      (acc: Record<string, string>, lineItem: LegacyLineItem) => {
        if (lineItem.type === LineItemType.Product && 'productId' in lineItem) {
          acc[lineItem.productId] = getProductImage(lineItem.productId);
        }
        return acc;
      },
      {},
    );
  }, [billingDetails, getProductImage]);

  return (
    <BillingDraftWidget
      cta={cta}
      onCTAClick={onCTAClick}
      widgetName={widgetName}
      buttonName={buttonName}
      avatarMap={avatarMap}
      billingDetails={billingDetails}
      source={selectedSource}
    />
  );
};
